import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Anchor, Box, Button, Group, Modal, Paper, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCloudUpload } from '@tabler/icons-react';
import { FilenameParser } from '../FilenameParser';
import { useUploadStore } from '../uploadStore';
import { IngestcfgSelector } from './IngestcfgSelector';
import { UploadTagsSection } from './UploadTagsSection';
import { VIDEO_EXTENSIONS } from '@/lib/constants/upload';
import type { UploadItem } from '@/lib/types/Upload';
import type { IngestcfgData } from '@/lib/types/Ingest';

interface UploadDropzoneProps {
  addFilesWithMeta: (filePairs: Array<{ file: File; uploadItemId: string }>) => void;
  ingestcfgs: IngestcfgData[];
  selectedIngestcfgId: number | null;
  onSelectIngestcfg: (id: number) => void;
  hasMultiple: boolean;
  isRestricted: boolean;
  cId: number;
  hasFiles: boolean;
}

const ACCEPT = VIDEO_EXTENSIONS.join(',');

function isValidVideoFile(filename: string): boolean {
  const ext = filename.lastIndexOf('.') >= 0
    ? filename.slice(filename.lastIndexOf('.')).toLowerCase()
    : '';
  return (VIDEO_EXTENSIONS as readonly string[]).includes(ext);
}

export function UploadDropzone({
  addFilesWithMeta,
  ingestcfgs,
  selectedIngestcfgId,
  onSelectIngestcfg,
  hasMultiple,
  isRestricted,
  cId,
  hasFiles,
}: UploadDropzoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Input WITHOUT accept: reliably opens the full document picker (folders,
  // Drive, real file names) where a video-typed accept list gets the photo
  // picker on mobile.
  const allFilesInputRef = useRef<HTMLInputElement>(null);
  // On touch devices the photo picker strips real file names (media-store ids
  // like 46127.mp4) and can't reach Drive — so the FULL file browser is the
  // mobile PRIMARY and the photo picker is the opt-in secondary. Desktop keeps
  // the filtered dialog as primary.
  const isCoarsePointer = useMemo(
    () => window.matchMedia('(pointer: coarse)').matches,
    [],
  );
  // Batch held for the pre-flight decision when names arrived stripped —
  // nothing is created or uploaded until the user chooses.
  const [heldFiles, setHeldFiles] = useState<File[] | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const addItem = useUploadStore((s) => s.addItem);

  // Derive selected profile and tag permissions
  const selectedProfile = useMemo(
    () => ingestcfgs.find((c) => c.ingestcfgId === selectedIngestcfgId) ?? null,
    [ingestcfgs, selectedIngestcfgId],
  );
  const defaultTags = selectedProfile?.tags ?? [];
  const canAddTags = selectedProfile?.allowAddTags ?? false;
  const canEditDefaultTags = selectedProfile?.allowEditDefaultTags ?? false;
  const showTagsSection = defaultTags.length > 0 || canAddTags || canEditDefaultTags;

  // Track effective tags for the current upload session
  const [effectiveTags, setEffectiveTags] = useState<string[]>([]);

  // Reset effective tags when profile selection changes
  useEffect(() => {
    setEffectiveTags(selectedProfile?.tags ?? []);
  }, [selectedIngestcfgId, selectedProfile?.tags]);

  const startFiles = useCallback(
    (validFiles: File[]) => {

      const filePairs: Array<{ file: File; uploadItemId: string }> = [];

      for (const file of validFiles) {
        const parsed = FilenameParser.parseMetadata(file.name, file.lastModified);
        const id = crypto.randomUUID();

        const item: UploadItem = {
          id,
          uppyFileId: '',
          cId,
          filename: file.name,
          fileSize: file.size,
          lastModified: file.lastModified || null,
          title: parsed.title,
          tags: [...effectiveTags],
          ingestcfgId: selectedIngestcfgId ?? 0,
          mediaId: 0,
          ingestJobId: 0,
          s3Key: '',
          uploadId: null,
          status: 'pending',
          uploadProgress: 0,
          processingProgress: 0,
          bytesUploaded: 0,
          bytesTotal: file.size,
          error: null,
          startedAt: null,
          completedAt: null,
          lastProgressAt: null,
          ingestingNotified: false,
        };

        addItem(item);
        filePairs.push({ file, uploadItemId: id });
      }

      addFilesWithMeta(filePairs);
    },
    [addItem, addFilesWithMeta, cId, selectedIngestcfgId, effectiveTags]
  );

  const processFiles = useCallback(
    (files: File[]) => {
      const validFiles = files.filter((f) => isValidVideoFile(f.name));
      // Unsupported picks must not vanish silently — the unfiltered file-browser
      // path (and drag-drop) can hand us anything.
      const skipped = files.filter((f) => !isValidVideoFile(f.name));
      if (skipped.length > 0) {
        notifications.show({
          color: 'orange',
          title: skipped.length === 1 ? 'File skipped' : 'Files skipped',
          message: `Not a supported video format: ${skipped.map((f) => f.name).join(', ')}`,
        });
      }
      if (validFiles.length === 0) return;
      // Pre-flight gate: digits-only names mean the photo picker replaced the
      // real file names. Hold the whole batch and ask BEFORE anything is
      // created or uploaded — no bytes wasted, nothing to cancel.
      if (validFiles.some((f) => FilenameParser.isStrippedName(f.name))) {
        setHeldFiles(validFiles);
        return;
      }
      startFiles(validFiles);
    },
    [startFiles]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const files = Array.from(e.dataTransfer.files);
      processFiles(files);
    },
    [processFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      processFiles(files);
      // Reset input so the same file can be re-added after cancel
      e.target.value = '';
    },
    [processFiles]
  );

  return (
    <Stack gap="sm">
      <IngestcfgSelector
        ingestcfgs={ingestcfgs}
        selectedIngestcfgId={selectedIngestcfgId}
        onSelect={onSelectIngestcfg}
        hasMultiple={hasMultiple}
        isRestricted={isRestricted}
      />

      {showTagsSection && (
        <UploadTagsSection
          defaultTags={defaultTags}
          effectiveTags={effectiveTags}
          onTagsChange={setEffectiveTags}
          canAddTags={canAddTags}
          canEditDefaultTags={canEditDefaultTags}
        />
      )}

      <Paper
        p="md"
        withBorder
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: dragOver
            ? 'var(--mantine-color-blue-5)'
            : 'var(--mantine-color-gray-3)',
          backgroundColor: dragOver
            ? 'var(--mantine-color-blue-0)'
            : undefined,
          cursor: 'pointer',
          transition: 'border-color 150ms, background-color 150ms',
          ...(hasFiles ? {} : { flex: 1, minHeight: 280 }),
        }}
        onClick={() => (isCoarsePointer ? allFilesInputRef : fileInputRef).current?.click()}
      >
        <Stack align="center" justify="center" gap="xs" style={{ height: '100%' }}>
          <IconCloudUpload
            size={hasFiles ? 28 : 48}
            color={dragOver ? 'var(--mantine-color-blue-5)' : 'var(--mantine-color-gray-5)'}
          />
          <Text size={hasFiles ? 'xs' : 'sm'} fw={500} c="dimmed">
            Drag and drop video files here
          </Text>
          <Button
            size="xs"
            variant="light"
            onClick={(e) => {
              e.stopPropagation();
              (isCoarsePointer ? allFilesInputRef : fileInputRef).current?.click();
            }}
          >
            Browse Files
          </Button>
          {!hasFiles && (
            <Text size="xs" c="dimmed">
              {VIDEO_EXTENSIONS.join(', ')}
            </Text>
          )}
          {/* Desktop gets no secondary: an unfiltered pick there could only
              admit files that validation rejects anyway (drag-drop covers the
              rest). On touch, the photo picker is the deliberate opt-in. */}
          {isCoarsePointer && (
            <Anchor
              component="button"
              type="button"
              size="xs"
              c="dimmed"
              underline="always"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current?.click();
              }}
            >
              Choose from your photos
            </Anchor>
          )}
        </Stack>
      </Paper>

      <Box display="none">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT}
          onChange={handleInputChange}
        />
        {/* No accept: guarantees the full document picker (Drive, folders,
            filenames) where the media-typed input gets the photo picker. */}
        <input
          ref={allFilesInputRef}
          type="file"
          multiple
          onChange={handleInputChange}
        />
      </Box>

      <Modal
        opened={heldFiles !== null}
        onClose={() => setHeldFiles(null)}
        title="Your device replaced these file names"
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            {(() => {
              const stripped = (heldFiles ?? []).filter((f) => FilenameParser.isStrippedName(f.name));
              const examples = stripped.slice(0, 3).map((f) => f.name).join(', ');
              return stripped.length === 1
                ? `One file arrived named ${examples} instead of its real file name.`
                : `${stripped.length} files arrived named ${examples}${stripped.length > 3 ? ', …' : ''} instead of their real file names.`;
            })()}{' '}
            Your phone's photo picker sends copies without the names you gave the
            files — uploading now will title the videos with those numbers. The
            file browser keeps your real file names.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                const files = heldFiles;
                setHeldFiles(null);
                if (files) startFiles(files);
              }}
            >
              Upload anyway
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setHeldFiles(null);
                allFilesInputRef.current?.click();
              }}
            >
              Pick again with the file browser
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
